import { cn } from "@/lib/utils";

/**
 * Central loading policy for the brand logo.
 *
 * Position semantics (auto-derived → loading + fetchPriority + decoding):
 *   - "header"  → above-the-fold, LCP-relevant (Navbar, Auth, Dashboard sidebar/topbar).
 *                 eager + fetchPriority="high" + decoding="sync"
 *   - "hero"    → primary brand mark on entry screens (Showcase, Gate).
 *                 eager + fetchPriority="high" + decoding="sync"
 *   - "inline"  → mid-page, but still visible in initial viewport (e.g. modals).
 *                 eager + fetchPriority="auto" + decoding="async"
 *   - "footer"  → below-the-fold (page footers, settings).
 *                 lazy + fetchPriority="low" + decoding="async"
 *
 * Use `position` instead of passing raw `loading`/`fetchPriority` so the
 * policy stays in one place. Manual overrides are still possible per prop
 * (escape hatch), but should be rare.
 */
export type LogoPosition = "header" | "hero" | "inline" | "footer";

interface LogoPolicy {
  loading: "eager" | "lazy";
  fetchPriority: "high" | "low" | "auto";
  decoding: "sync" | "async" | "auto";
}

const POLICY: Record<LogoPosition, LogoPolicy> = {
  header: { loading: "eager", fetchPriority: "high", decoding: "sync" },
  hero:   { loading: "eager", fetchPriority: "high", decoding: "sync" },
  inline: { loading: "eager", fetchPriority: "auto", decoding: "async" },
  footer: { loading: "lazy",  fetchPriority: "low",  decoding: "async" },
};

interface TableGuideLogoProps {
  className?: string;
  /** Accessible label, default "Table-Guide" */
  alt?: string;
  /**
   * Where the logo lives. Drives loading/fetchPriority/decoding via the
   * central POLICY above. Defaults to "inline" (safe middle ground).
   */
  position?: LogoPosition;
  /** Escape hatch — overrides the policy if explicitly set. */
  loading?: "eager" | "lazy";
  /** Escape hatch — overrides the policy if explicitly set. */
  fetchPriority?: "high" | "low" | "auto";
}

/**
 * Table-Guide Logo — transparent PNG with detailed gradients & 3D shadows.
 * Identical in light & dark mode (teal & copper sit on any background).
 *
 * Prefer `position` ("header" | "hero" | "inline" | "footer") over manual
 * `loading`/`fetchPriority` so the loading policy stays consistent across
 * the app. See POLICY in this file for the mapping.
 */
export const TableGuideLogo = ({
  className,
  alt = "Table-Guide",
  position = "inline",
  loading,
  fetchPriority,
}: TableGuideLogoProps) => {
  const policy = POLICY[position];
  const resolvedLoading = loading ?? policy.loading;
  const resolvedPriority = fetchPriority ?? policy.fetchPriority;
  const resolvedDecoding = resolvedLoading === "lazy" ? "async" : policy.decoding;

  const commonProps = {
    alt,
    loading: resolvedLoading,
    fetchPriority: resolvedPriority,
    decoding: resolvedDecoding,
    draggable: false,
  } as const;

  return (
    <picture className={cn("inline-block", className)}>
      {/* Light mode → logo on white background */}
      <img
        {...commonProps}
        src="/images/table-guide-logo-light.png"
        className={cn(
          "object-contain select-none pointer-events-none w-full h-full block dark:hidden",
        )}
      />
      {/* Dark mode → eigene Dark-Variante, ohne weißen Hintergrundkreis */}
      <img
        {...commonProps}
        src="/images/table-guide-logo-dark.png"
        className={cn(
          "object-contain select-none pointer-events-none w-full h-full hidden dark:block",
        )}
      />
    </picture>
  );
};

export default TableGuideLogo;
